import { useCallback, useEffect, useRef, useState } from 'react';

const MAX_RECORD_MS = 5 * 60 * 1000;

function pickMimeType() {
  const types = ['audio/webm;codecs=opus', 'audio/webm', 'audio/mp4'];
  if (typeof MediaRecorder === 'undefined') return '';
  return types.find((t) => MediaRecorder.isTypeSupported(t)) || '';
}

export function useAudioRecorder() {
  const [isRecording, setIsRecording] = useState(false);
  const [isSupported, setIsSupported] = useState(false);
  const mediaRecorderRef = useRef(null);
  const streamRef = useRef(null);
  const chunksRef = useRef([]);
  const stopTimerRef = useRef(null);

  useEffect(() => {
    setIsSupported(
      typeof navigator !== 'undefined'
        && !!navigator.mediaDevices?.getUserMedia
        && typeof MediaRecorder !== 'undefined'
    );
    return () => {
      if (stopTimerRef.current) clearTimeout(stopTimerRef.current);
      streamRef.current?.getTracks().forEach((t) => t.stop());
    };
  }, []);

  const stopStream = useCallback(() => {
    streamRef.current?.getTracks().forEach((t) => t.stop());
    streamRef.current = null;
  }, []);

  const stopRecording = useCallback(() => {
    if (stopTimerRef.current) {
      clearTimeout(stopTimerRef.current);
      stopTimerRef.current = null;
    }
    const recorder = mediaRecorderRef.current;
    if (recorder && recorder.state !== 'inactive') {
      recorder.stop();
    }
    setIsRecording(false);
  }, []);

  const startRecording = useCallback(() => {
    if (!isSupported) {
      return Promise.reject(new Error('Voice input is not supported in this browser.'));
    }
    if (isRecording) {
      return Promise.reject(new Error('Already recording.'));
    }

    return navigator.mediaDevices.getUserMedia({ audio: true }).then((stream) => {
      streamRef.current = stream;
      chunksRef.current = [];

      const mimeType = pickMimeType();
      const recorder = mimeType
        ? new MediaRecorder(stream, { mimeType })
        : new MediaRecorder(stream);

      mediaRecorderRef.current = recorder;

      return new Promise((resolve, reject) => {
        recorder.ondataavailable = (e) => {
          if (e.data.size > 0) chunksRef.current.push(e.data);
        };
        recorder.onerror = () => {
          stopStream();
          setIsRecording(false);
          reject(new Error('Recording failed.'));
        };
        recorder.onstop = () => {
          stopStream();
          const type = recorder.mimeType || 'audio/webm';
          const blob = new Blob(chunksRef.current, { type });
          chunksRef.current = [];
          mediaRecorderRef.current = null;
          resolve(blob);
        };

        recorder.start();
        setIsRecording(true);
        stopTimerRef.current = setTimeout(() => stopRecording(), MAX_RECORD_MS);
      });
    });
  }, [isRecording, isSupported, stopRecording, stopStream]);

  return {
    isSupported,
    isRecording,
    startRecording,
    stopRecording,
  };
}
